import React, { useState, useCallback, useRef } from 'react';
import {
  doc,
  setDoc,
  serverTimestamp,
  getDoc,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase/firebaseConfig';
import PhoneInput from './PhoneInput';
import './AddMemberForm.css';

// Constants

const KERALA_DISTRICTS = [
  'Thiruvananthapuram',
  'Kollam',
  'Pathanamthitta',
  'Alappuzha',
  'Kottayam',
  'Idukki',
  'Ernakulam',
  'Thrissur',
  'Palakkad',
  'Malappuram',
  'Calicut',     // Kozhikode
  'Wayanad',
  'Kannur',
  'Kasaragod',
];

const BLOOD_GROUPS = [
  'A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-', 'Unknown',
];

const ENGINE_VARIANTS = [
  '1.2L Petrol Manual',
  '1.2L Petrol Automatic',
  '1.2L CNG Manual',
  '1.2L CNG Automatic',
  '1.0L Turbo Petrol Manual',
  '1.0L Turbo Petrol Automatic',
];

const FRONX_VARIANTS = [
  'Sigma',
  'Delta',
  'Delta+',
  'Delta+ O',
  'Zeta',
  'Alpha',
];

const FRONX_COLORS = [
  'Nexa Blue',
  'Splendid Silver',
  'Grandeur Grey',
  'Opulent Red',
  'Arctic White',
  'Earthen Brown',
  'Cafe Noir (Dual Tone)',
  'Bluish Black (Dual Tone)',
];

// One year from today, formatted as YYYY-MM-DD for <input type="date">
const oneYearFromToday = () => {
  const d = new Date();
  d.setFullYear(d.getFullYear() + 1);
  return d.toISOString().slice(0, 10);
};

const INITIAL_FORM = {
  name:              '',
  phone_number:      '+91',
  email:             '',
  district:          '',
  car_variant:       '',
  car_color:         '',
  vehicle_number:    '',
  blood_group:       '',
  emergency_contact: '+91',
  engine_variant:    '',
  membership_expiry: oneYearFromToday(),
};

// Helper - generate a sequential member ID like "TF-2025-042"
const generateMemberId = () => {
  const year   = new Date().getFullYear();
  const suffix = Math.floor(Math.random() * 900) + 100; // 3-digit
  return `TF-${year}-${suffix}`;
};

// Component
export default function AddMemberForm() {
  const [form,       setForm]       = useState(INITIAL_FORM);
  const [errors,     setErrors]     = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [toast,      setToast]      = useState(null); // { type: 'success'|'error', message }
  const [photoFile,    setPhotoFile]    = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const fileInputRef = useRef(null);

  const handlePhotoChange = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setToast({ type: 'error', message: '❌ Please select an image file.' });
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setToast({ type: 'error', message: '❌ Photo must be under 2 MB.' });
      return;
    }
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  }, []);

  const clearPhoto = useCallback(() => {
    setPhotoFile(null);
    setPhotoPreview('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  // Validation
  const validate = useCallback(() => {
    const e = {};
    if (!form.name.trim())
      e.name = 'Full name is required.';

    // PhoneInput always emits "+91" + (0-10 digits). "+91" alone means
    // the user typed nothing.
    if (!form.phone_number.trim() || form.phone_number.trim() === '+91')
      e.phone_number = 'Phone number is required.';
    else if (!/^\+91\d{10}$/.test(form.phone_number.trim()))
      e.phone_number = 'Enter a valid 10-digit mobile number.';

    if (!form.email.trim())
      e.email = 'Email is required.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
      e.email = 'Enter a valid email address.';

    if (!form.district)
      e.district = 'Please select a district / chapter.';

    if (!form.car_variant)
      e.car_variant = 'Please select a Fronx variant.';

    if (!form.car_color)
      e.car_color = 'Please select a car colour.';

    if (!form.vehicle_number.trim())
      e.vehicle_number = 'Vehicle number is required.';

    if (!form.blood_group)
      e.blood_group = 'Please select a blood group.';

    if (!form.engine_variant)
      e.engine_variant = 'Please select an engine variant.';

    // Emergency contact now required.
    const ec = form.emergency_contact.trim();
    if (!ec || ec === '+91')
      e.emergency_contact = 'Emergency contact is required.';
    else if (!/^\+91\d{10}$/.test(ec))
      e.emergency_contact = 'Enter a valid 10-digit number.';

    return e;
  }, [form]);

  // Field change handler
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    // Clear field error on edit
    setErrors(prev => ({ ...prev, [name]: undefined }));
  }, []);

  // Submit - write to Firestore
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setSubmitting(true);
    setToast(null);

    const phoneKey = form.phone_number.trim();

    try {
      // Guard: check if member already exists before overwriting
      const existingRef = doc(db, 'allowed_users', phoneKey);
      const existingSnap = await getDoc(existingRef);
      if (existingSnap.exists()) {
        setToast({
          type: 'error',
          message: `⚠️ A member with ${phoneKey} already exists. Use Edit Member to update.`,
        });
        setSubmitting(false);
        return;
      }

      // Upload the photo first (if one was selected) so the
      // Firestore document already has the final download URL.
      let photoUrl = '';
      if (photoFile) {
        const ext = (photoFile.name.split('.').pop() || 'jpg').toLowerCase();
        const path = `profile_photos/${phoneKey}.${ext}`;
        const storageRef = ref(storage, path);
        await uploadBytes(storageRef, photoFile, {
          contentType: photoFile.type,
        });
        photoUrl = await getDownloadURL(storageRef);
      }

      // Build the Firestore document
      const memberId = generateMemberId();
      const memberDoc = {
        member_id:         memberId,
        name:              form.name.trim(),
        phone_number:      phoneKey,
        email:             form.email.trim().toLowerCase(),
        district:          form.district,
        car_variant:       form.car_variant,
        car_color:         form.car_color,
        vehicle_number:    form.vehicle_number.trim().toUpperCase(),
        blood_group:       form.blood_group || '',
        // Save as empty string if user left it on just the +91 prefix -
        // we don't want stray "+91" stored as a "valid" emergency contact.
        emergency_contact: form.emergency_contact.trim() === '+91'
          ? ''
          : form.emergency_contact.trim(),
        engine_variant:    form.engine_variant || '',
        membership_expiry: form.membership_expiry,
        profile_photo_url: photoUrl,
        status:            'active',
        // Admin-invited members are trusted: grant Premium tier + app access
        // by default. The signup flow (PendingApplicationsPage) sets these
        // from the tier the applicant paid for; admin invites bypass that.
        tier:              'premium',
        mobile_app_access: true,
        // next_service is empty at registration; can be updated later
        next_service: {
          due_date:       '',
          odometer_km:    0,
          service_center: '',
          notes:          '',
        },
        joined_on:   serverTimestamp(),
        invited_by:  'ADMIN',
      };

      // Document ID = phone number in E.164 format
      await setDoc(doc(db, 'allowed_users', phoneKey), memberDoc);

      setToast({
        type: 'success',
        message: `✅ ${form.name.trim()} (${memberId}) registered successfully!`,
      });
      setForm(INITIAL_FORM); // Reset form
      clearPhoto();
    } catch (err) {
      console.error('[AddMemberForm] Firestore write failed:', err);
      setToast({
        type: 'error',
        message: `❌ Registration failed: ${err.message}`,
      });
    } finally {
      setSubmitting(false);
    }
  }, [form, validate]);

  // Render
  return (
    <div className="amf-container">
      {/* Header */}
      <div className="amf-header">
        <img
          src="/logo.png"
          alt="Team Fronx"
          className="amf-logo-img"
          aria-hidden="true"
        />
        <div>
          <h1 className="amf-title">Register New Member</h1>
          <p className="amf-subtitle">
            Kerala's Premier Fronx Community — Admin Portal
          </p>
        </div>
      </div>

      {/* Toast notification */}
      {toast && (
        <div className={`amf-toast amf-toast--${toast.type}`} role="alert">
          {toast.message}
          <button
            className="amf-toast-close"
            onClick={() => setToast(null)}
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      {/* Form */}
      <form className="amf-form" onSubmit={handleSubmit} noValidate>

        {/* ── Section 1: Personal Information ─────────────────── */}
        <SectionHeader title="Personal Information" />

        <div className="amf-field">
          <label htmlFor="name" className="amf-label">
            Full Name <span className="amf-required">*</span>
          </label>
          <input
            id="name"
            name="name"
            type="text"
            className={`amf-input ${errors.name ? 'amf-input--error' : ''}`}
            placeholder="e.g. Arjun Menon"
            value={form.name}
            onChange={handleChange}
            autoComplete="name"
          />
          {errors.name && <p className="amf-error-text">{errors.name}</p>}
        </div>

        <div className="amf-field">
          <label htmlFor="phone_number" className="amf-label">
            Phone Number <span className="amf-required">*</span>
          </label>
          <PhoneInput
            value={form.phone_number}
            onChange={(v) => {
              setForm((p) => ({ ...p, phone_number: v }));
              setErrors((p) => ({ ...p, phone_number: undefined }));
            }}
            error={!!errors.phone_number}
            inputProps={{ id: 'phone_number', name: 'phone_number' }}
          />
          {errors.phone_number && (
            <p className="amf-error-text">{errors.phone_number}</p>
          )}
          <p className="amf-hint">
            10-digit Indian mobile number. Becomes the login key.
          </p>
        </div>

        <div className="amf-field">
          <label htmlFor="email" className="amf-label">
            Email <span className="amf-required">*</span>
          </label>
          <input
            id="email"
            name="email"
            type="email"
            className={`amf-input ${errors.email ? 'amf-input--error' : ''}`}
            placeholder="member@example.com"
            value={form.email}
            onChange={handleChange}
            autoComplete="email"
          />
          {errors.email && <p className="amf-error-text">{errors.email}</p>}
        </div>

        <div className="amf-field">
          <label className="amf-label">
            Profile Photo <span className="amf-optional">(optional)</span>
          </label>

          {photoPreview ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                padding: 12,
                background: '#161618',
                border: '1px solid rgba(212, 181, 116, 0.18)',
                borderRadius: 8,
              }}
            >
              <img
                src={photoPreview}
                alt="Preview"
                style={{
                  width: 72,
                  height: 72,
                  objectFit: 'cover',
                  borderRadius: 8,
                  border: '2px solid #D4B574',
                }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  color: '#F7E8BF',
                  fontSize: 13,
                  fontWeight: 600,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}>
                  {photoFile?.name}
                </div>
                <div style={{ color: '#B79F6D', fontSize: 11, marginTop: 2 }}>
                  {photoFile && (photoFile.size / 1024).toFixed(0)} KB
                </div>
              </div>
              <button
                type="button"
                onClick={clearPhoto}
                style={{
                  padding: '6px 12px',
                  background: 'transparent',
                  border: '1px solid #E63946',
                  borderRadius: 6,
                  color: '#E63946',
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Remove
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              style={{
                width: '100%',
                padding: '24px 16px',
                background: '#161618',
                border: '2px dashed rgba(212, 181, 116, 0.35)',
                borderRadius: 8,
                color: '#D8C8A4',
                fontSize: 13,
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <span style={{ fontSize: 24 }}>📷</span>
              <span style={{ fontWeight: 600 }}>Click to upload photo</span>
              <span style={{ color: '#B79F6D', fontSize: 11, marginTop: 4 }}>
                JPG, PNG, or WebP · max 2 MB
              </span>
              <span style={{
                color: '#D8C8A4',
                fontSize: 11,
                marginTop: 2,
                textAlign: 'center',
                lineHeight: 1.4,
              }}>
                Recommended: <strong>square 500×500 px</strong>, face centred,
                <br />good lighting, plain background.
              </span>
            </button>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handlePhotoChange}
          />
        </div>

        {/* ── Section 2: Membership ───────────────────────────── */}
        <SectionHeader title="Membership" />

        <div className="amf-field">
          <label htmlFor="district" className="amf-label">
            District <span className="amf-required">*</span>
          </label>
          <select
            id="district"
            name="district"
            className={`amf-select ${errors.district ? 'amf-input--error' : ''}`}
            value={form.district}
            onChange={handleChange}
          >
            <option value="" disabled>Select district…</option>
            {KERALA_DISTRICTS.map(d => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
          {errors.district && (
            <p className="amf-error-text">{errors.district}</p>
          )}
        </div>

        <div className="amf-field">
          <label htmlFor="membership_expiry" className="amf-label">
            Membership Expiry <span className="amf-required">*</span>
          </label>
          <input
            id="membership_expiry"
            name="membership_expiry"
            type="date"
            className="amf-input"
            value={form.membership_expiry}
            onChange={handleChange}
          />
          <p className="amf-hint">
            Defaults to one year from today. Admins can renew or change later.
          </p>
        </div>

        {/* ── Section 3: Vehicle Details ──────────────────────── */}
        <SectionHeader title="Vehicle Details" />

        <div className="amf-field">
          <label htmlFor="vehicle_number" className="amf-label">
            Vehicle Number <span className="amf-required">*</span>
          </label>
          <input
            id="vehicle_number"
            name="vehicle_number"
            className={`amf-input ${errors.vehicle_number ? 'amf-input--error' : ''}`}
            placeholder="e.g. KL-11-AA-1234"
            style={{ textTransform: 'uppercase' }}
            value={form.vehicle_number}
            onChange={handleChange}
          />
          {errors.vehicle_number && (
            <p className="amf-error-text">{errors.vehicle_number}</p>
          )}
        </div>

        <div className="amf-field">
          <label htmlFor="car_variant" className="amf-label">
            Fronx Variant <span className="amf-required">*</span>
          </label>
          <select
            id="car_variant"
            name="car_variant"
            className={`amf-select ${errors.car_variant ? 'amf-input--error' : ''}`}
            value={form.car_variant}
            onChange={handleChange}
          >
            <option value="" disabled>Select variant…</option>
            {FRONX_VARIANTS.map(v => (
              <option key={v} value={v}>{v}</option>
            ))}
          </select>
          {errors.car_variant && (
            <p className="amf-error-text">{errors.car_variant}</p>
          )}
        </div>

        <div className="amf-field">
          <label htmlFor="engine_variant" className="amf-label">
            Engine Variant <span className="amf-required">*</span>
          </label>
          <select
            id="engine_variant"
            name="engine_variant"
            className="amf-select"
            value={form.engine_variant}
            onChange={handleChange}
          >
            <option value="">— Select engine —</option>
            {ENGINE_VARIANTS.map(v => (
              <option key={v} value={v}>{v}</option>
            ))}
          </select>
        </div>

        <div className="amf-field">
          <label htmlFor="car_color" className="amf-label">
            Car Colour <span className="amf-required">*</span>
          </label>
          <select
            id="car_color"
            name="car_color"
            className={`amf-select ${errors.car_color ? 'amf-input--error' : ''}`}
            value={form.car_color}
            onChange={handleChange}
          >
            <option value="" disabled>Select colour…</option>
            {FRONX_COLORS.map(c => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          {errors.car_color && (
            <p className="amf-error-text">{errors.car_color}</p>
          )}
        </div>

        {/* ── Section 4: Emergency Information ────────────────── */}
        <SectionHeader title="Emergency Information" />

        <div className="amf-field">
          <label htmlFor="blood_group" className="amf-label">
            Blood Group <span className="amf-required">*</span>
          </label>
          <select
            id="blood_group"
            name="blood_group"
            className={`amf-select ${errors.blood_group ? 'amf-input--error' : ''}`}
            value={form.blood_group}
            onChange={handleChange}
          >
            <option value="" disabled>Select blood group…</option>
            {BLOOD_GROUPS.map(b => (
              <option key={b} value={b}>{b}</option>
            ))}
          </select>
          {errors.blood_group && (
            <p className="amf-error-text">{errors.blood_group}</p>
          )}
        </div>

        <div className="amf-field">
          <label htmlFor="emergency_contact" className="amf-label">
            Emergency Contact <span className="amf-required">*</span>
          </label>
          <PhoneInput
            value={form.emergency_contact}
            onChange={(v) => {
              setForm((p) => ({ ...p, emergency_contact: v }));
              setErrors((p) => ({ ...p, emergency_contact: undefined }));
            }}
            error={!!errors.emergency_contact}
            inputProps={{ id: 'emergency_contact', name: 'emergency_contact' }}
          />
          <p className="amf-hint">
            Family member or close friend to call in case of emergency.
          </p>
          {errors.emergency_contact && (
            <p className="amf-error-text">{errors.emergency_contact}</p>
          )}
        </div>

        {/* Submit */}
        <button
          type="submit"
          className="amf-submit-btn"
          disabled={submitting}
        >
          {submitting ? (
            <>
              <span className="amf-spinner" aria-hidden="true" />
              Registering…
            </>
          ) : (
            'Register Member'
          )}
        </button>
      </form>
    </div>
  );
}

function SectionHeader({ title }) {
  return (
    <div
      style={{
        marginTop: 28,
        marginBottom: 14,
        paddingBottom: 8,
        borderBottom: '1px solid rgba(212, 181, 116, 0.18)',
        fontSize: 11,
        letterSpacing: 1.8,
        textTransform: 'uppercase',
        color: '#D4B574',
        fontWeight: 700,
      }}
    >
      {title}
    </div>
  );
}
